export const useAuth = () => {
  const authStore = useAuthStore()
  const config = useRuntimeConfig()

  // ========== 用户登录相关 ==========
  
  /**
   * 用户登录（用户名密码）
   */
  const userLogin = async (
    username: string,
    password: string,
    captchaClick: { x: number; y: number } | null
  ) => {
    try {
      const response = await $fetch('/api/auth/user-login', {
        method: 'POST',
        body: {
          username,
          password,
          captcha_click: captchaClick,
        },
      })

      return response
    } catch (error: any) {
      console.error('用户登录失败:', error)
      return {
        success: false,
        message: error.data?.message || error.message || '登录失败'
      }
    }
  }

  /**
   * 用户登出
   */
  const userLogout = async () => {
    try {
      await $fetch('/api/auth/user-logout', {
        method: 'POST'
      })
    } catch (error) {
      console.error('登出失败:', error)
    } finally {
      authStore.clearAuth()
      // 整页跳转，避免 keep-alive 缓存导致仍停留在受保护页
      window.location.href = '/auth/login'
    }
  }

  /**
   * 获取用户信息
   */
  const fetchUserInfo = async () => {
    await authStore.fetchUserInfo()
  }

  // ========== 千川授权相关 ==========

  /**
   * 获取千川授权URL（通过服务端API，确保使用运行时环境变量）
   */
  const getAuthUrl = async (): Promise<string> => {
    try {
      console.log('[千川授权] 客户端调用服务端API获取授权URL')
      const response = await $fetch('/api/auth/qianchuan-auth-url', {
        method: 'GET'
      }) as any
      
      if (response.success && response.authUrl) {
        console.log('[千川授权] 从服务端获取授权URL成功:', response.authUrl)
        return response.authUrl
      } else {
        throw new Error(response.message || '获取授权URL失败')
      }
    } catch (error: any) {
      console.error('[千川授权] 获取授权URL失败:', error)
      throw error
    }
  }

  /**
   * 跳转到千川授权页面
   */
  const loginToQianchuan = async () => {
    try {
      const authUrl = await getAuthUrl()
      if (true) {
        window.location.href = authUrl
      }
    } catch (error: any) {
      console.error('[千川授权] 跳转失败:', error)
      alert('获取授权URL失败: ' + (error.message || '未知错误'))
    }
  }

  /**
   * 处理千川授权回调
   */
  const handleAuthCallback = async (code: string, state: string) => {
    try {
      const response = await $fetch('/api/auth/callback', {
        method: 'GET',
        query: { code, state }
      })

      if (response.success) {
        await authStore.fetchAuthStatus()
        return { success: true }
      }
      
      return { success: false, error: (response as any).message || '授权回调处理失败' }
    } catch (error) {
      console.error('处理授权回调失败:', error)
      return { success: false, error }
    }
  }

  /**
   * 管理员切换到指定用户账号
   */
  const switchUser = async (targetUserId: number) => {
    try {
      const response = await $fetch<{ success: boolean; message: string }>('/api/admin/switch-user', {
        method: 'POST',
        body: { targetUserId }
      })

      if (response.success) {
        // 切换后需要刷新用户信息和千川认证状态
        authStore.clearAuth()
        await authStore.fetchUserInfo()
        await authStore.fetchAuthStatus()
        // 跳转到首页
        navigateTo('/advertisers')
        return { success: true }
      }
      return { success: false, message: response.message }
    } catch (error: any) {
      console.error('切换用户失败:', error)
      return { success: false, message: error.data?.message || error.message || '切换用户失败' }
    }
  }

  /**
   * 从代登状态切回管理员账号
   */
  const switchBack = async () => {
    try {
      const response = await $fetch<{ success: boolean; message: string }>('/api/admin/switch-back', {
        method: 'POST'
      })

      if (response.success) {
        authStore.clearAuth()
        await authStore.fetchUserInfo()
        await authStore.fetchAuthStatus()
        navigateTo('/admin/users')
        return { success: true }
      }
      return { success: false, message: response.message }
    } catch (error: any) {
      console.error('切回管理员失败:', error)
      return { success: false, message: error.data?.message || error.message || '切回管理员失败' }
    }
  }

  /**
   * 统一登出（用户登录和千川授权）
   */
  const logout = () => {
    userLogout()
  }

  // ========== 计算属性 ==========

  const isAuthenticated = computed(() => {
    return authStore.isAuthenticated
  })

  const hasQianchuanAuth = computed(() => {
    return authStore.hasQianchuanAuth
  })

  const currentUser = computed(() => {
    return authStore.user
  })

  const isImpersonating = computed(() => authStore.impersonating)
  const adminUser = computed(() => authStore.adminUser)

  return {
    // 用户登录
    userLogin,
    userLogout,
    fetchUserInfo,
    
    // 千川授权
    loginToQianchuan,
    getAuthUrl,
    handleAuthCallback,
    
    // 管理员代登
    switchUser,
    switchBack,
    
    // 统一方法
    logout,
    
    // 状态
    isAuthenticated,
    hasQianchuanAuth,
    currentUser,
    isImpersonating,
    adminUser
  }
}

